import gql from "graphql-tag";
import { useEffect, useState } from "react";

import { useCustomerKey } from "utils/customHooks/useCustomerKey";

import { FETCH_POLICY } from "../../segments/AggregatesGetter/aggregatesGetterUtils";
import useQueryWithErrorHandling from "utils/useQuery";
import { midgardClients } from "providers/MidgardProvider";
import {
  DEFAULT_SHIFTS_GETTER_UPDATE_INTERVAL,
  FAST_RETRY_INTERVAL,
} from "constants/intervals";
import { useDispatch } from "react-redux";
import { updateManyConstraintLeaderProcessIds } from "providers/GlobalStateProvider";
import {
  getPgKey,
  useIsAdmin,
  useGetPageFromPath,
  useMidgardIdHubletPage,
  useSummaryGroup,
  useTrendsPageFilterState,
  useWorkScheduleIdOnCurrentPage,
} from "utils/customHooks/genericHooks";
import { PROCESS_GROUPS_QUERY_TITLE } from "contexts/ErrorManager/errorManagerUtils";
import { ProcessGroupFromMidgard, ProcessFromMidgard } from "./midgardTypes";
import { atom, useAtom } from "jotai";
import { useSelector } from "store/useSelector";
import { useLocation } from "react-use";
import { INVALID_CUSTOMERS, getIsCypressRunning } from "constants/constants";
import { useFlightConfig } from "./FlightConfig/useFlightConfig";
import { useCachedProcessGroups } from "components/Dashboard/Tiles/ProcessTile/processTileUtils";
import { ProcessGroup } from "types/types";
import { ONE_DAY_OF_MS } from "utils/timeUtils";
import { mapEntries } from "@raven/shared/utils";

// ! Note about getPgKey and some potential incoming 🐛s:
// since midgard process name and machemetry process shortName don't always match up,
// I lowercase them and remove any spaces, though it may not work 100%,
// I believe this is only a temporary issue until we remove machemetry from flight (soon)

/** first query only fetches what's needed for the group tiles to become interactive
 * (removed `currentCallState`, which significantly slows the response)
 */
export const PROCESS_GROUPS_QUERY_CHEAP = gql`
  query ProcessGroups {
    processGroups(groupType: "FLIGHT") {
      id
      name
      shortName
      groupSortIdx
      constraintProcesses {
        id
        description
        taps {
          name
          machemetryId
        }
      }
      leaderProcess {
        id
        description
        taps {
          name
          machemetryId
        }
      }
      processes {
        id
        description
        taps {
          name
          machemetryId
        }
        workScheduleConfig {
          id
        }
      }
      tags
      types
      processesSortOrder
    }
    processes {
      id
      description
    }
  }
`;
// e.g. https://raveng.gke-us-east1.raven-cloud.com/midgard/graphiql#query=query%20ProcessGroups%20%7B%0A%20%20processGroups%20%7B%0A%20%20%20%20id%0A%20%20%20%20name%0A%20%20%20%20shortName%0A%20%20%20%20processes%20%7B%0A%20%20%20%20%20%20id%0A%20%20%20%20%20%20description%0A%20%20%20%20%20%20taps%20%7B%0A%20%20%20%20%20%20%20%20name%0A%20%20%20%20%20%20%20%20machemetryId%0A%20%20%20%20%20%20%7D%0A%20%20%20%20%7D%0A%20%20%20%20tags%0A%20%20%20%20types%0A%20%20%7D%0A%20%20processes%20%7B%0A%20%20%20%20id%0A%20%20%20%20description%0A%20%20%7D%0A%7D%0A&variables=&operationName=ProcessGroups
export const PROCESS_GROUPS_QUERY = gql`
  query ProcessGroups {
    processGroups(groupType: "FLIGHT") {
      id
      name
      shortName
      groupSortIdx
      constraintProcesses {
        id
        description
        taps {
          name
          machemetryId
        }
      }
      leaderProcess {
        id
        description
        taps {
          name
          machemetryId
        }
      }
      processes {
        id
        description
        currentCallState {
          callStartTime
          callTarget
        }
        workScheduleConfig {
          id
        }
        taps {
          id
          name
          machemetryId
        }
      }
      tags
      types
      processesSortOrder
    }
    processes {
      id
      description
      workScheduleConfig {
        id
      }
    }
  }
`;
export const PROCESS_GROUPS_FOR_REPORTS_QUERY = gql`
  query ProcessGroups {
    processGroups {
      id
      name
      shortName
      groupSortIdx
      constraintProcesses {
        id
        description
        taps {
          name
          machemetryId
        }
      }
      leaderProcess {
        id
        description
        taps {
          name
          machemetryId
        }
      }
      processes {
        id
        description
        currentCallState {
          callStartTime
          callTarget
        }
        workScheduleConfig {
          id
        }
        taps {
          id
          name
          machemetryId
        }
      }
      tags
      types
      processesSortOrder
    }
    processes {
      id
      description
      workScheduleConfig {
        id
      }
    }
  }
`;

const INITIAL_POLL_INTERVAL = DEFAULT_SHIFTS_GETTER_UPDATE_INTERVAL;
/** fetches Midgard's ProcessGroups and stores it in the GlobalStateProvider store
 */
const ProcessGroupsGetter = () => {
  const ckey = useCustomerKey();
  const midgardIdHubletPage = useMidgardIdHubletPage();
  const shouldUseCheapQuery = useShouldUseCheapQuery();
  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  const { isAllWorkSchedulesPage } = useGetPageFromPath();
  const shouldFetch =
    ckey &&
    !INVALID_CUSTOMERS.includes(ckey) &&
    (workScheduleId ||
      isAllWorkSchedulesPage ||
      window.location.pathname.includes("/timeline/") ||
      midgardIdHubletPage);
  return shouldFetch ? (
    <>
      <Query
        key={
          // force refetch once after we get the cheap, interactive tiles
          shouldUseCheapQuery ? 1 : 0
        }
      />
      <VisibleGroupsSetter />
    </>
  ) : null;
};

const HIDDEN_PROCESS_GROUP_NAMES = ["Amcycle Test"];
export const allProcessGroupsAtom = atom<ProcessGroupFromMidgard[]>([]);
export const didFetchProcessGroupsAtom = atom<
  "noneFetched" | "cheapTilesFetched" | "fullTilesFetched"
>("noneFetched");
function Query() {
  const [, setCachedProcessGroups] = useCachedProcessGroups();
  const [pollInterval, setPollInterval] = useState(INITIAL_POLL_INTERVAL);
  const ckey = useCustomerKey();
  const client = midgardClients.get(ckey);
  const [
    {
      general: { multiplyNetworkRequestIntervals },
    },
  ] = useFlightConfig();
  const dispatch = useDispatch();
  const [didFetchProcessGroups, setDidFetchProcessGroups] = useAtom(
    didFetchProcessGroupsAtom
  );
  const shouldUseCheapQuery = useShouldUseCheapQuery();
  const isAdminUser = useIsAdmin();
  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  const { isTrendsPage, isAllWorkSchedulesPage } = useGetPageFromPath();
  const midgardIdHubletPage = useMidgardIdHubletPage();
  const [, setAllProcessGroups] = useAtom(allProcessGroupsAtom);
  useQueryWithErrorHandling(
    PROCESS_GROUPS_QUERY_TITLE,
    shouldUseCheapQuery ? PROCESS_GROUPS_QUERY_CHEAP : PROCESS_GROUPS_QUERY,
    {
      // run it if the trends page is not filtered to "all" shifts
      ...(pollInterval
        ? { pollInterval: pollInterval * multiplyNetworkRequestIntervals }
        : {}),
      client,
      fetchPolicy: FETCH_POLICY,
      onError: err => {
        // if we get an error, try again in 10 seconds
        setPollInterval(FAST_RETRY_INTERVAL);
        console.log(`Process groups query gone wrong. cKey: ${ckey}`);
        console.log(JSON.stringify(err));
      },
      onCompleted: (data: {
        processGroups: ProcessGroupFromMidgard[];
        processes: ProcessFromMidgard[];
      }) => {
        if (!data.processGroups) {
          return;
        }
        const processGroups = data.processGroups
          .filter(pg => {
            if (isAdminUser || midgardIdHubletPage) {
              return true;
            } else {
              return !HIDDEN_PROCESS_GROUP_NAMES.includes(
                pg.name || pg.shortName
              );
            }
          })
          .filter(pg => {
            // trends page allows switching workschedules to view any groups
            if (isTrendsPage || midgardIdHubletPage) {
              return true;
            }
            const isProcessInCurrentSchedule =
              pg.processes[0]?.workScheduleConfig?.id == workScheduleId ||
              isAllWorkSchedulesPage;
            // on other pages, only return groups with processes in the current schedule
            return isProcessInCurrentSchedule;
          })
          .map(pg => ({
            ...pg,
            // first, dispatch cheap tiles (with empty call state) to load interactive tiles quickly
            ...(shouldUseCheapQuery
              ? {
                  processes: pg.processes.map(p => ({
                    ...p,
                    currentCallState: {
                      callTarget: undefined,
                    },
                  })),
                }
              : {}),
          }));
        const processGroupsMapped = mapMidgardProcessGroups(
          processGroups as ProcessGroupFromMidgard[],
          ckey
        );

        dispatch({
          type: "UPDATE_MIDGARD_PROCESS_GROUPS",
          payload: {
            processGroups: processGroupsMapped,
          },
        });
        const now = Date.now();
        setCachedProcessGroups(prev => {
          const processGroupsToCache = processGroupsMapped.map(pg => ({
            ...pg,
            callStart: null,
            asOf: now,
            processes: pg.processes.map(p => ({
              ...p,
              callStart: null, // ignore callStart in cached process groups (prevents call icon flicker)
            })),
          }));

          const nextProcessGroups: typeof prev = {
            ...prev,
            [ckey]: {
              ...prev[ckey],
              [workScheduleId]: processGroupsToCache,
            },
          };

          // filter out any groups that were cached >24h ago, so we don't show stale data
          const stillValid = cachedProcessGroups => {
            if (!Array.isArray(cachedProcessGroups)) {
              return [];
            }

            return cachedProcessGroups.filter(
              pg => pg.asOf && pg.asOf > now - ONE_DAY_OF_MS
            );
          };

          const nextProcessGroupsFiltered = mapEntries(
            nextProcessGroups,
            ([ckey, processGroupsByWorkSchedule]) => [
              ckey,
              mapEntries(
                processGroupsByWorkSchedule,
                ([workScheduleId, processGroups]) => [
                  workScheduleId,
                  stillValid(processGroups),
                ]
              ),
            ]
          );

          return nextProcessGroupsFiltered;
        });

        const groupsContraintsData = data.processGroups
          .map(pg => {
            const pgKey = getPgKey(pg.name || pg.shortName);
            // ? batch this dispatch as a potential performance improvement
            const constraintProcessesFiltered = pg.constraintProcesses
              .map(cp => cp.taps?.[0]?.machemetryId ?? null)
              .filter(Boolean) as string[];
            if (
              constraintProcessesFiltered.length > 0 ||
              pg.leaderProcess?.taps?.[0]?.machemetryId
            ) {
              return {
                constraintProcessIds: constraintProcessesFiltered,
                leaderProcessId:
                  pg.leaderProcess?.taps?.[0]?.machemetryId ?? null,
                groupName: pgKey,
              };
            }
            return null;
          })
          .filter(Boolean) as {
          constraintProcessIds: string[];
          leaderProcessId: string | null;
          groupName: string;
        }[];
        dispatch(updateManyConstraintLeaderProcessIds(groupsContraintsData));
        if (didFetchProcessGroups === "noneFetched") {
          setDidFetchProcessGroups("cheapTilesFetched");
        } else if (didFetchProcessGroups === "cheapTilesFetched") {
          setDidFetchProcessGroups("fullTilesFetched");
        }
        setPollInterval(INITIAL_POLL_INTERVAL);

        setAllProcessGroups(data.processGroups);
      },
    }
  );

  return null;
}

export default ProcessGroupsGetter;

function useShouldUseCheapQuery() {
  const [didFetchProcessGroups] = useAtom(didFetchProcessGroupsAtom);
  const {
    isProcessTilesPage,
    isGridLayoutPage,
    isTimelinePage,
    isOperatorPage,
    isHubletPage,
  } = useGetPageFromPath();
  const midgardIdHubletPage = useMidgardIdHubletPage();
  const isSingleProcessGroupPage =
    isProcessTilesPage ||
    isTimelinePage ||
    isGridLayoutPage ||
    isOperatorPage ||
    isHubletPage ||
    midgardIdHubletPage;

  return (
    (!getIsCypressRunning() && isSingleProcessGroupPage) ||
    didFetchProcessGroups === "noneFetched"
  );
}

/** whenever we change pages, set visible groups accordingly, e.g. set to all groups on group tiles page */
export function VisibleGroupsSetter() {
  const summaryGroup = useSummaryGroup();
  const allGroups = useSelector(state => state.legacy.allGroups);
  const dispatch = useDispatch();
  const { search, pathname } = useLocation();
  const {
    trendsPageFilterState: { workScheduleId: trendsPageFilterWorkScheduleId },
  } = useTrendsPageFilterState();
  const {
    isHubletPage,
    isProcessTilesPage,
    isTimelinePage,
    isGroupTilesPage,
    isTrendsPage,
    isReportsPage,
    isGridLayoutPage,
    isOperatorPage,
    isTier0Page,
    isAllWorkSchedulesPage,
  } = useGetPageFromPath();
  const midgardIdHubletPage = useMidgardIdHubletPage();
  useEffect(() => {
    if (
      isAllWorkSchedulesPage ||
      window.location.pathname.includes("/timeline/") ||
      midgardIdHubletPage
    ) {
      dispatch({
        type: "UPDATE_VISIBLE_GROUPS",
        payload: allGroups,
      });
    } else if (pathname?.includes("fromSnapshots")) {
      // from snapshots page, we send processes through the url
      const searchParams = new URLSearchParams(search);
      const urlWorkScheduleConfigId =
        searchParams.get("workSchedule") ?? trendsPageFilterWorkScheduleId;
      const urlProcessIds = searchParams.get("processes");
      const urlProcessGroupIdsOrNames = searchParams.get("processGroups");
      const groupsInWorkSchedule = allGroups.filter(pg => {
        if (urlWorkScheduleConfigId) {
          if (
            pg.processes[0]?.workScheduleConfig?.id == urlWorkScheduleConfigId
          ) {
            return true;
          }
          return false;
        } else {
          return true;
        }
      });
      const allProcessesInWorkSchedule = groupsInWorkSchedule.flatMap(
        pg => pg.processes
      );
      const processesInUrl = urlProcessIds
        ?.split(",")
        .map(pid =>
          allProcessesInWorkSchedule.find(p =>
            [p.id, p.midgardId].includes(pid)
          )
        );
      if (processesInUrl?.some(p => !p)) {
        console.log(
          "⚠ no processes found associated with these ids:",
          urlProcessIds,
          "in",
          allProcessesInWorkSchedule
        );
      }
      const processesInGroupsInUrl = urlProcessGroupIdsOrNames
        ?.split(",")
        .flatMap(pgId => {
          const group = groupsInWorkSchedule.find(pg =>
            [pg.id, pg.shortName, pg.longName].includes(pgId)
          );
          return group?.processes ?? [];
        });
      if (processesInGroupsInUrl?.some(p => !p)) {
        console.log(
          "⚠ no processes found associated with these group ids:",
          urlProcessGroupIdsOrNames,
          "in",
          groupsInWorkSchedule
        );
      }
      if (processesInUrl?.length && allGroups[0]) {
        dispatch({
          type: "UPDATE_VISIBLE_GROUPS",
          payload: [{ ...allGroups[0], processes: processesInUrl }],
        });
      } else if (processesInGroupsInUrl?.length && allGroups[0]) {
        const matchingGroup =
          allGroups.find(pg =>
            pg.processes.find(p => p.name === processesInGroupsInUrl[0].name)
          ) ?? allGroups[0];
        dispatch({
          type: "UPDATE_VISIBLE_GROUPS",
          payload: [{ ...matchingGroup, processes: processesInGroupsInUrl }],
        });
      }
    } else if (isTier0Page) {
      // on tier0 page we only want one process visible at a time; process and workSchedule are stored in the url
      const searchParams = new URLSearchParams(search);
      const urlWorkScheduleConfigId =
        searchParams.get("workSchedule") ?? trendsPageFilterWorkScheduleId;
      const urlProcessName = searchParams.get("process") ?? "";
      const groupsInWorkSchedule = allGroups.filter(pg => {
        if (urlWorkScheduleConfigId) {
          if (
            pg.processes[0]?.workScheduleConfig?.id == urlWorkScheduleConfigId
          ) {
            return true;
          }
          return false;
        } else {
          return true;
        }
      });
      const groupInUrl = groupsInWorkSchedule.find(pg =>
        pg.processes.find(p => p.name === urlProcessName)
      );
      const processInUrl = groupInUrl?.processes.find(
        p => p.name === urlProcessName
      );
      if (processInUrl) {
        dispatch({
          type: "UPDATE_VISIBLE_GROUPS",
          payload: [{ ...groupInUrl, processes: [processInUrl] }],
        });
      }
    } else if (
      // we are on a dashboard page (i.e. lower down than Group Tiles page)
      summaryGroup &&
      (isProcessTilesPage ||
        isTimelinePage ||
        isHubletPage ||
        isGridLayoutPage ||
        isOperatorPage ||
        isTier0Page)
    ) {
      const currentGroup = allGroups.find(g => {
        return g.summaryGroup === summaryGroup;
      });
      if (currentGroup) {
        dispatch({
          type: "UPDATE_VISIBLE_GROUPS",
          payload: [currentGroup],
        });
      }
    } else if (
      // we are on trends, group tiles, reports pages
      isGroupTilesPage ||
      isTrendsPage ||
      isReportsPage
    ) {
      const searchParams = new URLSearchParams(search);
      const urlWorkScheduleConfigId =
        searchParams.get("workSchedule") ?? trendsPageFilterWorkScheduleId;
      const groupsInWorkSchedule = allGroups.filter(pg => {
        if (urlWorkScheduleConfigId) {
          if (
            pg.processes[0]?.workScheduleConfig?.id == urlWorkScheduleConfigId
          ) {
            return true;
          }
          return false;
        } else {
          return true;
        }
      });

      const processOrProcessGroupNameReportsPage =
        getReportPageProcessOrGroupNameFromPath(pathname);
      const isReportsListingPage =
        processOrProcessGroupNameReportsPage === "reports";

      let groupsInWorkScheduleAndProcessGroup = groupsInWorkSchedule;
      // if we are on a report for a specific group, only show that group
      if (
        isReportsPage &&
        !isReportsListingPage &&
        processOrProcessGroupNameReportsPage
      ) {
        groupsInWorkScheduleAndProcessGroup = groupsInWorkSchedule.filter(
          pg => {
            // if we're on a report for...
            // ...a specific group -> only show that group
            const isThisProcessGroup = [
              pg.longName,
              pg.shortName,
              pg.summaryGroup,
            ].includes(processOrProcessGroupNameReportsPage);
            if (isThisProcessGroup) {
              return true;
            }
            // ...a specific process -> only show that process's group
            const matchedProcessInThisGroup = pg.processes.find(process => {
              return process.name === processOrProcessGroupNameReportsPage;
            });
            if (matchedProcessInThisGroup) {
              return true;
            }
            return false;
          }
        );
      }

      dispatch({
        type: "UPDATE_VISIBLE_GROUPS",
        payload: groupsInWorkScheduleAndProcessGroup,
      });
    }
  }, [
    allGroups,
    dispatch,
    search,
    isGroupTilesPage,
    isGridLayoutPage,
    isProcessTilesPage,
    isTimelinePage,
    isHubletPage,
    isTrendsPage,
    summaryGroup,
    isOperatorPage,
    isReportsPage,
    trendsPageFilterWorkScheduleId,
    pathname,
    isTier0Page,
    isAllWorkSchedulesPage,
    midgardIdHubletPage,
  ]);

  // reset fetched state when we go to group tiles page
  const [didFetchProcessGroups, setDidFetchProcessGroups] = useAtom(
    didFetchProcessGroupsAtom
  );
  // when we change pages, if we just landed on group tiles page, reset fetched state
  const location = useLocation();
  useEffect(() => {
    if (isGroupTilesPage && didFetchProcessGroups === "fullTilesFetched") {
      setDidFetchProcessGroups("noneFetched");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.href]);

  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  // reset whenever we switch workschedule
  useEffect(() => {
    setDidFetchProcessGroups("noneFetched");
  }, [workScheduleId, setDidFetchProcessGroups]);

  return null;
}
/** grab e.g. "Actif" from url like /chem/reports/eos/Actif?ge=1705985700000&lt=1706010900000&workSchedule=1 -> on reports pages this can be a process group name or process name */
export function getReportPageProcessOrGroupNameFromPath(
  pathname: string | undefined
) {
  const pathnameSplit = pathname?.split("/");
  const processOrProcessGroupNameReportsPage = decodeURIComponent(
    pathnameSplit?.[pathnameSplit.length - 1] ?? ""
  );
  return processOrProcessGroupNameReportsPage;
}

export function mapMidgardProcessGroups(
  midgardProcessGroups: ProcessGroupFromMidgard[],
  ckey: string
): ProcessGroup[] {
  return midgardProcessGroups.map((midgardGroup: ProcessGroupFromMidgard) => ({
    id: midgardGroup.id,
    groupSortIdx: String(midgardGroup.groupSortIdx),
    processesSortOrder: midgardGroup.processesSortOrder?.map(
      // convert midgard id to machemetry id for sorting on Process Tiles panel
      midgardId =>
        midgardGroup.processes.find(p => p.id === midgardId)?.taps?.[0]
          ?.machemetryId ?? ""
    ),
    summaryGroup: midgardGroup.name,
    processes: midgardGroup.processes.map(midgardProcess => ({
      midgardId: midgardProcess.id,
      id: midgardProcess.taps?.[0]?.machemetryId ?? "",
      name: midgardProcess.description,
      isSelected: true,
      ckey,
      workScheduleConfig: midgardProcess.workScheduleConfig,
      showCountNotMetricOnGroupTile: false as boolean,
      taps: midgardProcess.taps ?? [],
    })),
    longName: midgardGroup.name,
    shortName: midgardGroup.shortName || midgardGroup.name,
    output: null,
    outputLabel: "",
    showCountNotMetricOnGroupTile: false as boolean,
    isIdleUntagged: midgardGroup.processes.reduce(
      (acc, process) =>
        acc ||
        Boolean(
          process.currentCallState.callTarget &&
            process.currentCallState.callStartTime
        ),
      false
    ),
    ckey,
    // use the earliest callStart of all the processes in the group
    callStart: midgardGroup.processes.reduce(
      (acc, process) =>
        acc === null ||
        (process.currentCallState.callStartTime &&
          process.currentCallState.callStartTime < acc)
          ? process.currentCallState.callStartTime
          : acc,
      null as number | null
    ),
  }));
}
